import { PROFESSIONAL_URL } from '@modules/business-manager/config/constants';
import type { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { getProfessionalHeroImage } from '@modules/professional/util/get-professional-hero-image';
import {
    PROFILE_LEAD_MAX_CHARS,
    paragraphsOf,
    splitLead,
} from '@modules/professional/lookbook/profile-text';
import type { DownloadFilters } from '@modules/professional/config/download-filters';
import type { Lookbook } from './LookbookTiles';

type Professional = NonNullable<
    ReturnType<typeof useProfessionalInitialRouteData>
>;

/**
 * The professional's own lookbook as a tile, its cover worded as the PDF's is:
 * the opening of their summary (or their name), then their discipline and city.
 */
export const profileLookbook = (
    professional: Professional,
    tabBase: string,
): Lookbook => {
    const { lead } = splitLead(
        paragraphsOf(professional.BlockSummary),
        PROFILE_LEAD_MAX_CHARS,
    );
    const city =
        (professional.LibraryLocations ?? []).find(
            (location) => !location.LocationHidden,
        )?.City ?? '';

    return {
        __typename: 'ProfessionalLookbook',
        isProfileBook: true,
        ItemID: professional.ID,
        Title: professional.Title,
        CoverTitle: professional.Title,
        Link: tabBase,
        // The PDF is served by the public professional route only — the
        // Business Manager preview tree has no lookbook route, so a book built
        // from the current tab base 404s for a practice previewing its own
        // page. Navigation still follows the tab base; the file does not.
        LookbookUrl: `${PROFESSIONAL_URL.PUBLIC}/${professional.URLSegment}/lookbook.pdf`,
        CoverImageUrl:
            getProfessionalHeroImage(professional.BannerImages) ?? null,
        Subheading: null,
        IntroLead: null,
        statement: lead || professional.Title,
        imprint: [professional.PrimaryCategory?.PrettyTitle, city]
            .filter(Boolean)
            .join(' / '),
        subtitle: 'Profile lookbook',
    };
};

/**
 * Whether the profile's book belongs in the current view: it is company-level
 * and has no file category, so a category filter or an item-type filter without
 * company files leaves it out, and a search keeps it only when it names them.
 */
export const showsProfileLookbook = (
    filters: DownloadFilters,
    professionalTitle: string,
): boolean => {
    if (filters.page > 1 || filters.fileCategories.length > 0) return false;
    if (
        filters.itemTypes.length > 0 &&
        !filters.itemTypes.includes('Profile')
    ) {
        return false;
    }
    const search = filters.search.trim().toLowerCase();
    return !search || professionalTitle.toLowerCase().includes(search);
};
